<template>
  <div
    :class="
      classes(
        n(),
      )
    "
    role="radiogroup"
  >
    <!-- 使用 options 渲染 -->
    <template v-if="options && options.length">
      <Radio
        v-for="(option, index) in normalizedOptions"
        :key="index"
        :value="getOptionValue(option)"
        :label="getOptionLabel(option)"
        :disabled="disabled || getOptionDisabled(option)"
        :checked="innerValue === getOptionValue(option)"
        :allow-uncheck="allowUncheck"
        :margin-less="marginLess"
        @change="handleRadioChange"
      />
    </template>
    <!-- 使用 slot 渲染 -->
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, provide } from 'virtual-vue';
import { radioGroupProps } from './radio-group-props';
import { emits, useEmits } from './emits';
import { createNamespace } from '../utils/components';
import { useVModel } from '../hooks/use-v-model';
import Radio from './radio.vue';
import type { RadioOption, RadioOptionObj } from './radio';
import { RADIO_GROUP_KEY } from './constants';

const { name, n, classes } = createNamespace('radio-group');

export default defineComponent({
  name,
  components: {
    Radio,
  },
  props: radioGroupProps,
  emits,
  setup(props) {
    const emit = useEmits();
    const innerValue = useVModel(props);

    // 标准化 options
    const normalizedOptions = computed(() => {
      if (!props.options) return [];
      return props.options;
    });

    // 获取选项的值
    const getOptionValue = (option: RadioOption): string | number | boolean => {
      if (typeof option === 'object') {
        const valueKey = props.keys?.value || 'value';
        return (option as RadioOptionObj)[valueKey] || option.value;
      }
      return option;
    };

    // 获取选项的标签
    const getOptionLabel = (option: RadioOption): string => {
      if (typeof option === 'object') {
        const labelKey = props.keys?.label || 'label';
        return (option as RadioOptionObj)[labelKey] || option.label || String(option.value);
      }
      return String(option);
    };

    // 获取选项的禁用状态
    const getOptionDisabled = (option: RadioOption): boolean => {
      if (typeof option === 'object') {
        const disabledKey = props.keys?.disabled || 'disabled';
        return (option as RadioOptionObj)[disabledKey] || option.disabled || false;
      }
      return false;
    };

    // 处理单个 Radio 的变化
    const handleRadioChange = (value: string | number | boolean | undefined) => {
      if (value === undefined && props.allowUncheck) {
        innerValue.value = undefined;
        emit('change', undefined);
      } else if (value !== undefined) {
        innerValue.value = value;
        emit('change', value);
      }
    };

    // 提供给子 Radio 组件的上下文
    const radioGroupContext = {
      value: innerValue,
      disabled: computed(() => props.disabled),
      allowUncheck: computed(() => props.allowUncheck),
      marginLess: computed(() => props.marginLess),
      onChange: handleRadioChange,
    };

    provide(RADIO_GROUP_KEY, radioGroupContext);

    return {
      n,
      classes,
      innerValue,
      normalizedOptions,
      getOptionValue,
      getOptionLabel,
      getOptionDisabled,
      handleRadioChange,
    };
  },
});
</script>
